import { cn } from "@hey404/ui";
import { Photo } from "./photo";
import { Play } from "lucide-react";
import { btnPrimary } from "./admin-ui";
import { Doodle, Ribbon, titleClass } from "./design-system";
import { upcoming } from "./upcoming-content";

/** One lesson as the overview shows it. */
export interface OverviewLesson {
  id: string;
  title: string;
  thumbnailUrl?: string | null;
  durationMinutes: number;
  moduleNumber: string;
  moduleTitle: string;
  /** Lesson body (Markdown); the card shows a plain-text excerpt. */
  content?: string;
}

// First readable paragraph of the lesson's Markdown: headings, images,
// quotes and list markers dropped, links reduced to their text.
function excerpt(markdown = "", max = 260) {
  const paragraph =
    markdown
      .split(/\n\s*\n/)
      .map((block) =>
        block
          .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
          .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
          .replace(/^\s*(#{1,6}|>|[-*+]|\d+\.)\s+/gm, "")
          .replace(/[*_`~]/g, "")
          .trim(),
      )
      .find((block) => block.length > 40) ?? "";
  return paragraph.length > max
    ? `${paragraph.slice(0, max).replace(/\s+\S*$/, "")}…`
    : paragraph;
}

export interface ContinueWatchingProps {
  courseTitle: string;
  lesson: OverviewLesson;
  /** Seconds already watched; 0 when the lesson hasn't been started. */
  positionSeconds: number;
  /**
   * `first`: nothing watched yet; `resume`: a lesson left halfway;
   * `next`: the last one was finished, this is the one that follows.
   */
  mode: "first" | "resume" | "next";
  onPlay: (lessonId: string, startAt: number) => void;
}

/**
 * "Sigue viendo": the lesson the student was watching — its thumbnail on one
 * side, what the lesson is about on the other — with how far they got and a
 * resume button that reopens the player at that second.
 */
export function ContinueWatching({
  courseTitle,
  lesson,
  positionSeconds,
  mode,
  onPlay,
}: ContinueWatchingProps) {
  const totalSeconds = lesson.durationMinutes * 60;
  const watched = totalSeconds
    ? Math.min(100, Math.round((positionSeconds / totalSeconds) * 100))
    : 0;
  const description = excerpt(lesson.content);
  const copy = {
    first: { heading: "Empieza por aquí", action: "Empezar la clase" },
    resume: { heading: "Sigue viendo tu clase", action: "Seguir viendo" },
    next: { heading: "Tu siguiente clase", action: "Ir a la clase" },
  }[mode];
  const minutesLeft = Math.max(
    1,
    Math.ceil((totalSeconds - positionSeconds) / 60),
  );
  return (
    <section aria-labelledby="sigue-viendo">
      <p className="mt-16 flex items-center gap-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
        <span
          aria-hidden="true"
          className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
        />
        {courseTitle}
      </p>
      <h2 id="sigue-viendo" className={cn(titleClass.section, "mb-7 mt-3")}>
        {copy.heading}
        <span aria-hidden="true" className="text-bubble ml-2">
          ✳
        </span>
      </h2>
      <div className="border-ink bg-bone border-surface shadow-brutal-lg relative grid overflow-visible md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <span
          aria-hidden="true"
          className="washi -top-3 left-1/2 z-20 -translate-x-1/2 md:left-[76%]"
          style={{ rotate: "-4deg" }}
        />
        <button
          type="button"
          onClick={() => onPlay(lesson.id, positionSeconds)}
          aria-label={`${copy.action}: ${lesson.title}`}
          className="bg-ink border-ink md:border-r-(length:--border-surface) border-b-(length:--border-surface) group relative block aspect-video cursor-pointer overflow-hidden md:aspect-auto md:min-h-80 md:border-b-0"
        >
          {lesson.thumbnailUrl && (
            <Photo
              src={lesson.thumbnailUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          {/* Kept small and translucent: lesson thumbnails carry the title card
              in the middle of the frame, and a full-size badge covered it. It
              turns solid on hover, when the click is about to happen. Set at
              54% rather than 50% because the card's title text sits below its
              window bar, so a true centre reads as floating above the text. */}
          <span className="border-ink bg-bubble border-chip absolute left-1/2 top-[54%] grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full opacity-60 transition-[transform,opacity] duration-200 group-hover:scale-110 group-hover:opacity-100">
            <Play size={20} fill="currentColor" className="ml-0.5" />
          </span>
          {mode === "resume" && (
            <span className="bg-ink/40 absolute inset-x-0 bottom-0 h-2">
              <span
                className="bg-bubble block h-full"
                style={{ width: `${watched}%` }}
              />
            </span>
          )}
        </button>
        <div className="flex flex-col justify-center p-6 sm:p-8">
          <p className="text-ink-soft font-mono text-xs font-bold uppercase tracking-[0.06em]">
            Módulo {lesson.moduleNumber} · {lesson.moduleTitle}
          </p>
          <h3 className={cn(titleClass.featured, "mt-2")}>{lesson.title}</h3>
          {description && <p className="mt-4 leading-relaxed">{description}</p>}
          <p className="text-ink-soft mt-4 font-mono text-xs">
            {mode === "first"
              ? `${lesson.durationMinutes} min · tu primera clase`
              : mode === "resume" && watched > 0
                ? `Vas en el ${watched}% · te quedan ${minutesLeft} min`
                : `${lesson.durationMinutes} min · la que sigue en tu curso`}
          </p>
          <button
            type="button"
            onClick={() => onPlay(lesson.id, positionSeconds)}
            className={`${btnPrimary} mt-6 w-full justify-between px-5 py-3 text-base`}
          >
            {copy.action} <span aria-hidden="true">↗</span>
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * "Próximamente", duplicated from the landing's "Lo que viene" (content in
 * ./upcoming-content): filed cards with the category tab, the campaign photo
 * with route, number and arrow, then status, title, outcome and a disabled
 * "Próximamente" button. The platform keeps its crossed moving tapes above.
 * Static on purpose: it always shows, whatever the courses' state in the
 * admin.
 */
export function ComingSoon() {
  return (
    <section aria-labelledby="proximamente" className="mt-20">
      {/* Crossed tape, like a "coming soon" site barrier: wider than the
          column and clipped sideways, so the tilted ends never show. */}
      <div
        aria-hidden="true"
        className="relative -mx-5 h-28 overflow-x-clip sm:-mx-8 lg:-mx-12"
      >
        <Ribbon
          items={["Próximamente"]}
          tilt={-3}
          seconds={6}
          className="absolute -inset-x-16 top-4"
        />
        <Ribbon
          items={["Próximamente"]}
          tilt={2.5}
          seconds={7}
          reverse
          tone="bubble"
          className="absolute -inset-x-16 top-12"
        />
      </div>
      <div className="mt-10 flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <p className="font-mono text-sm font-bold uppercase tracking-[0.06em]">
            {upcoming.eyebrow}
          </p>
          <h2 id="proximamente" className={cn(titleClass.section, "mt-4")}>
            {upcoming.title}
          </h2>
        </div>
        <div className="flex max-w-xs items-center gap-4 font-mono text-sm">
          <span
            aria-hidden="true"
            className="font-display text-7xl font-extrabold leading-[0.8]"
          >
            +
          </span>
          <p>{upcoming.note}</p>
        </div>
      </div>
      <p className="text-ink-soft mt-6 max-w-xl font-mono">
        {upcoming.description}
      </p>
      <div className="mt-12 grid items-start gap-x-6 gap-y-12 pr-2 md:grid-cols-3">
        {upcoming.courses.map((course) => (
          <article
            key={course.slug}
            className="future-card"
            data-accent={course.accent}
          >
            <div className="future-file-tab">{course.category}</div>
            <div className="future-cover has-photo" aria-hidden="true">
              <span className="future-route">Ruta {course.route}</span>
              <span className="future-cover-number">{course.route}</span>
              <span className="future-cover-mark">↗</span>
              <img
                src={course.photo}
                alt=""
                width={960}
                height={640}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="course-photo"
              />
            </div>
            <div className="future-card-copy">
              <p className="future-status">
                <span aria-hidden="true" />
                {upcoming.status}
              </p>
              <h3>{course.title}</h3>
              <p className="future-outcome">{course.outcome}</p>
              <button type="button" disabled className="future-availability">
                {upcoming.status}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export interface StartCourseProps {
  /** The course to start; null when the student has none yet. */
  course: {
    title: string;
    description: string;
    lessons: number;
    minutes: number;
  } | null;
  /** Shown when the student has no course: the one on sale, by name. */
  suggestion?: { title: string; href: string } | null;
  onStart: () => void;
}

/**
 * The "Sigue viendo" slot before there is a lesson to resume: same card
 * shape, inviting the student to start their course — or, with no course
 * yet, to get to know the one on sale.
 */
export function StartCourse({ course, suggestion, onStart }: StartCourseProps) {
  const title = course?.title ?? suggestion?.title;
  return (
    <section aria-labelledby="empieza-curso">
      <p className="mt-16 flex items-center gap-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
        <span
          aria-hidden="true"
          className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
        />
        Tu curso
      </p>
      <h2 id="empieza-curso" className={cn(titleClass.section, "mb-7 mt-3")}>
        {course ? "Empieza tu curso" : "Empieza tu primer curso"}
        <span aria-hidden="true" className="text-bubble ml-2">
          ✳
        </span>
      </h2>
      <div className="border-ink bg-bone border-surface shadow-brutal-lg relative grid overflow-visible md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <span
          aria-hidden="true"
          className="washi -top-3 left-1/2 z-20 -translate-x-1/2 md:left-[76%]"
          style={{ rotate: "-4deg" }}
        />
        <button
          type="button"
          onClick={onStart}
          aria-label={title ? `Empezar ${title}` : "Empezar un curso"}
          className="bg-bubble platform-grid border-ink md:border-r-(length:--border-surface) border-b-(length:--border-surface) group relative block aspect-video cursor-pointer overflow-hidden md:aspect-auto md:min-h-80 md:border-b-0"
        >
          <Doodle
            name="flower-yellow"
            float
            tilt={-10}
            className="absolute -left-4 -top-4 w-24"
          />
          <Doodle
            name="cursor-cream"
            tilt={-14}
            className="absolute bottom-6 right-8 w-14"
          />
          <span className="border-ink bg-paper border-surface shadow-brutal absolute inset-0 m-auto grid h-20 w-20 place-items-center rounded-full transition-transform duration-200 group-hover:scale-110">
            <Play size={30} fill="currentColor" className="ml-1" />
          </span>
        </button>
        <div className="flex flex-col justify-center p-6 sm:p-8">
          <p className="text-ink-soft font-mono text-xs font-bold uppercase tracking-[0.06em]">
            {course ? "Inicia el curso" : "Todavía no tienes un curso activo"}
          </p>
          <h3 className={cn(titleClass.featured, "mt-2")}>
            {title ?? "Tu primer curso te espera"}
          </h3>
          {course?.description && (
            <p className="mt-4 leading-relaxed">{course.description}</p>
          )}
          <p className="text-ink-soft mt-4 font-mono text-xs">
            {course
              ? course.lessons
                ? `${course.lessons} clases · ${course.minutes} min · a tu ritmo`
                : "Las primeras clases llegan muy pronto."
              : "Conoce de qué se trata y empieza cuando quieras."}
          </p>
          <button
            type="button"
            onClick={onStart}
            className={`${btnPrimary} mt-6 w-full justify-between px-5 py-3 text-base`}
          >
            {course ? "Empezar el curso" : "Conocer el curso"}{" "}
            <span aria-hidden="true">↗</span>
          </button>
        </div>
      </div>
    </section>
  );
}

export interface CertificateProgressProps {
  studentName: string;
  /** The course the certificate is for; null when the student has none. */
  course: { title: string; completed: number; total: number } | null;
  /** Label + action of the button: where the student should go next. */
  action: { label: string; onClick: () => void };
}

/**
 * Certificados before one is issued: a preview of the certificate the
 * student is working towards (their name, the course, an "en curso" seal)
 * beside how far they are and a button to the lesson that gets them closer.
 */
export function CertificateProgress({
  studentName,
  course,
  action,
}: CertificateProgressProps) {
  const total = course?.total ?? 0;
  const completed = course?.completed ?? 0;
  const progress = total ? Math.round((completed / total) * 100) : 0;
  const left = Math.max(total - completed, 0);
  const heading = !course
    ? "Tu primer certificado te espera"
    : !total
      ? "Tu certificado llega con las clases"
      : left === 0
        ? "Tu certificado se está emitiendo"
        : left === 1
          ? "Te falta 1 clase"
          : `Te faltan ${left} clases`;
  return (
    <div className="mt-10 grid items-center gap-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <figure aria-hidden="true" className="relative m-0 pr-3">
        <div className="border-ink bg-bone border-surface shadow-brutal-xl rotate-2 p-3">
          <div className="border-ink border-chip p-5 sm:p-7">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <img src="/logo-final.svg" alt="" className="h-9 w-auto" />
              <p className="font-mono text-xs font-bold uppercase tracking-[0.06em]">
                Certificado de finalización
              </p>
            </div>
            <p className="text-ink-soft mt-8 font-mono text-xs uppercase tracking-[0.16em]">
              Certificamos que
            </p>
            <p className="border-ink font-display mt-2 border-b-2 pb-3 text-4xl font-extrabold">
              {studentName}
            </p>
            <p className="mt-4 text-sm">
              completó el curso <strong>{course?.title ?? "de hey404"}</strong>
            </p>
            <div className="mt-7 flex flex-wrap items-end justify-between gap-4">
              <p className="border-ink border-t-(length:--border-surface) pt-2 font-mono text-xs uppercase">
                Equipo hey404
              </p>
              <span className="border-ink bg-acid border-surface font-display grid size-24 rotate-[8deg] place-items-center rounded-full text-center text-xs font-extrabold leading-tight">
                EN CURSO
                <br />
                {progress}%
              </span>
            </div>
          </div>
        </div>
        <span className="border-ink bg-lemon border-surface shadow-brutal -ml-2 -mt-3 inline-block rotate-[-3deg] px-3 py-2 font-mono text-xs font-bold uppercase">
          Vista previa
        </span>
      </figure>
      <div className="min-w-0">
        <p className="flex items-center gap-2.5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
          <span
            aria-hidden="true"
            className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
          />
          Tu certificado
        </p>
        <h2 className={cn(titleClass.section, "mt-3")}>{heading}</h2>
        {course && total > 0 && (
          <>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={completed}
              aria-label="Clases completadas"
              className="border-ink bg-paper border-chip mt-6 h-4 overflow-hidden rounded-full"
            >
              <span
                className="bg-ink block h-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-ink-soft mt-2 font-mono text-xs">
              {completed} de {total} clases completadas
            </p>
          </>
        )}
        <p className="mt-5 leading-relaxed">
          {course
            ? total
              ? "Cuando completes todas las clases publicadas, tu certificado se emite solo, con tu nombre y un código de verificación."
              : "Apenas se publiquen las clases podrás empezar; al completarlas, tu certificado se emite solo."
            : "Inscríbete en un curso y, al completarlo, recibes tu certificado con un código de verificación."}
        </p>
        <ul className="border-ink mt-5 grid gap-2 border-t-2 pt-4 text-sm">
          <li className="flex gap-2">
            <strong aria-hidden="true">✓</strong> Listo para compartir en
            LinkedIn o descargar en PDF
          </li>
          <li className="flex gap-2">
            <strong aria-hidden="true">✓</strong> Verificable con su código
            único
          </li>
        </ul>
        <button
          type="button"
          onClick={action.onClick}
          className={`${btnPrimary} mt-7 w-full justify-between px-5 py-3 text-base sm:w-auto sm:min-w-64`}
        >
          {action.label} <span aria-hidden="true">↗</span>
        </button>
      </div>
    </div>
  );
}
